import { useState, useEffect, useRef } from 'react';
import { Paragraph, Label, Button } from '@/components/atoms';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/atoms';
import { Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { TwoFactorDialogWrapper } from './two-factor-dialog-wrapper';
import { useSetupTwoFactor, useVerifyTwoFactor } from '@/api';
import type { TwoFactorSetupData } from '@/api/mutations/auth/response';

interface AuthenticatorSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSuccess: () => void;
}

export const AuthenticatorSetup = ({
  isOpen,
  onClose,
  onBack,
  onSuccess,
}: AuthenticatorSetupProps) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);

  const {
    mutate: triggerSetup,
    isPending: isSettingUp,
    isError: isSetupError,
    error: setupError,
  } = useSetupTwoFactor((data) => {
    if (data.data) {
      setSetupData(data.data);
    }
  });

  const { mutate: verifyTwoFactor, isPending: isVerifying } =
    useVerifyTwoFactor(() => {
      onSuccess();
    });

  const initialized = useRef(false);

  useEffect(() => {
    if (isOpen && !setupData && !initialized.current) {
      initialized.current = true;
      triggerSetup({ method: 'totp' });
    }
  }, [isOpen, setupData, triggerSetup]);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Backup code copied to clipboard');
  };

  const handleVerify = () => {
    if (otp.length !== 6) {
      setError('Invalid code. please try again');
      return;
    }
    verifyTwoFactor({ token: otp });
  };

  const isLoading = isSettingUp && !setupData;

  return (
    <TwoFactorDialogWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={isSetupError ? 'SETUP FAILED' : 'SCAN THE QR CODE'}
      onBack={onBack}
      onContinue={handleVerify}
      continueLabel="Verify & Continue"
      continueDisabled={otp.length !== 6 || isLoading || !!isSetupError}
      isLoading={isVerifying}
      showBackButton
    >
      <div className="flex flex-col gap-6 items-center">
        {/* Error State */}
        {isSetupError && (
          <div className="flex flex-col gap-4 py-8 items-center">
            <p className="text-red-500 text-sm text-center">
              {setupError instanceof Error
                ? setupError.message
                : 'Failed to generate QR code. Please try again.'}
            </p>
            <Button
              onClick={() => {
                initialized.current = false;
                triggerSetup({ method: 'totp' });
              }}
              size="sm"
            >
              Retry Setup
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && !isSetupError && (
          <div className="flex flex-col gap-6 items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <Paragraph className="text-sm text-gray-600">
              Generating QR code...
            </Paragraph>
          </div>
        )}

        {/* Content State */}
        {!isLoading && !isSetupError && setupData && (
          <>
            {/* QR Code */}
            {setupData.qrCodeDataURL && (
              <div className="w-64 h-64 border-4 border-gray-200 rounded-lg p-4 flex items-center justify-center bg-white">
                <img
                  src={setupData.qrCodeDataURL}
                  alt="QR Code"
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            <Paragraph className="text-sm text-gray-600 text-center max-w-lg">
              Scan with Google authenticator app, Authy, or similar app
            </Paragraph>

            {/* Backup Codes */}
            {setupData.backupCodes && setupData.backupCodes.length > 0 && (
              <div className="w-full max-w-lg">
                <Label className="text-sm font-medium mb-2 block">
                  Backup Codes (Save these securely):
                </Label>
                <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg">
                  {setupData.backupCodes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                    >
                      <span className="font-mono text-sm">{code}</span>
                      <Button
                        onClick={() => handleCopyCode(code)}
                        className="p-1 transition-colors"
                        aria-label="Copy backup code"
                        variant="ghost"
                        size="sm"
                      >
                        <Copy className="w-3 h-3 text-gray-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 w-full max-w-lg">
              <Label className="text-sm font-medium">
                Enter the 6-digit code from your app
                {error && (
                  <span className="text-red-500 text-xs ml-auto float-right">
                    {error}
                  </span>
                )}
              </Label>
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  setError('');
                }}
              >
                <InputOTPGroup className="gap-2 justify-center w-full">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      className={error ? 'border-red-500 bg-red-50' : ''}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          </>
        )}
      </div>
    </TwoFactorDialogWrapper>
  );
};
